/**
 * Burbuja de un mensaje.
 *
 * La direccion decide el lado (cliente a la izquierda, salientes a la derecha),
 * el autor decide el color (cliente, agente humano o bot) y el estado decide el
 * tick de entrega. Los tipos no textuales se muestran con una etiqueta legible.
 */
import { memo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { MessageView } from '@shared/api-types.ts';
import { formatDateTime, formatTime } from '../format.ts';
import { AUTHOR_TYPE_LABELS, MESSAGE_STATUS_LABELS, messagePreview } from '../state.ts';
import { MediaAttachment } from './MediaAttachment.tsx';

/**
 * Texto del error ya listo para pintar.
 *
 * `errorTitle` y `errorDetail` pueden ser el mismo texto: para los codigos de
 * Meta que no estan traducidos, el backend usa el texto generico en los dos
 * campos. Cuando uno contiene al otro se muestra una sola vez (el mas largo,
 * que es el que lleva el arreglo), y solo cuando son distintos se separan en
 * titulo y detalle.
 */
function readError(message: MessageView): { code: string | null; primary: string; secondary: string | null } {
  const code = message.errorCode?.trim() ? message.errorCode.trim() : null;
  const title = message.errorTitle?.trim() ? message.errorTitle.trim() : null;
  const detail = message.errorDetail?.trim() ? message.errorDetail.trim() : null;

  if (title && detail) {
    const normalizedTitle = normalizeErrorText(title);
    const normalizedDetail = normalizeErrorText(detail);
    if (normalizedDetail.includes(normalizedTitle)) return { code, primary: detail, secondary: null };
    if (normalizedTitle.includes(normalizedDetail)) return { code, primary: title, secondary: null };
    return { code, primary: title, secondary: detail };
  }

  return { code, primary: title ?? detail ?? 'El mensaje no se pudo enviar', secondary: null };
}

function normalizeErrorText(text: string): string {
  return text.replace(/\s+/g, ' ').toLowerCase();
}

function Tick({ message }: { message: MessageView }): ReactElement {
  if (message.status === 'failed') {
    const { code, primary } = readError(message);
    return (
      <span className="tick tick--failed" title={code ? `Error ${code}: ${primary}` : primary}>
        ⚠
      </span>
    );
  }
  if (message.status === 'read') {
    return (
      <span className="tick tick--read" title={MESSAGE_STATUS_LABELS.read}>
        ✓✓
      </span>
    );
  }
  if (message.status === 'delivered') {
    return (
      <span className="tick" title={MESSAGE_STATUS_LABELS.delivered}>
        ✓✓
      </span>
    );
  }
  if (message.status === 'sent') {
    return (
      <span className="tick" title={MESSAGE_STATUS_LABELS.sent}>
        ✓
      </span>
    );
  }
  return (
    <span className="tick tick--pending" title={MESSAGE_STATUS_LABELS[message.status]}>
      {message.status === 'queued' ? 'en cola' : 'enviando…'}
    </span>
  );
}

function MediaTag({ children }: { children: ReactNode }): ReactElement {
  return <span className="media__tag">{children}</span>;
}

/**
 * Bloque de error de un mensaje fallido.
 *
 * El codigo de Meta va siempre delante del texto y sin truncar: es el dato que
 * el usuario busca en internet o pega en soporte. Si no hay codigo no se
 * inventa ninguno: solo se muestra el texto.
 */
function MessageError({ message }: { message: MessageView }): ReactElement {
  const { code, primary, secondary } = readError(message);

  return (
    <div className="bubble__error">
      <p className="bubble__error-title">
        {code ? <span className="bubble__error-code">Error {code} </span> : null}
        {primary}
      </p>
      {secondary ? <p className="bubble__error-detail">{secondary}</p> : null}
    </div>
  );
}

/**
 * Contenido de un mensaje multimedia.
 *
 * Cuando el fichero esta disponible se pinta el reproductor (audio y video), la
 * miniatura (imagen y sticker) o el boton de descarga (documento). Si el mensaje
 * se guardo sin los datos del adjunto quedan la etiqueta y el tipo, que es toda
 * la informacion que hay.
 */
function renderMedia(
  message: MessageView,
  kind: 'image' | 'audio' | 'video' | 'document' | 'sticker',
  label: string,
): ReactElement {
  const media = message.media;
  // Sin el identificador de Meta no hay nada que pedir: la ruta de multimedia
  // solo sirve ficheros de mensajes entrantes.
  const playable = Boolean(media?.id);

  return (
    <>
      <MediaTag>{label}</MediaTag>
      {playable && media ? (
        <MediaAttachment
          messageId={message.id}
          kind={kind}
          media={media}
          textColor={mediaTextColor(message)}
        />
      ) : (
        <>
          {media?.filename ? <p className="bubble__text">{media.filename}</p> : null}
          {media?.mimeType ? <p className="media__meta">{media.mimeType}</p> : null}
          <p className="media__meta">Sin datos del archivo adjunto</p>
        </>
      )}
      {media?.caption ? <p className="bubble__text">{media.caption}</p> : null}
    </>
  );
}

/** Color del texto de los botones del reproductor, segun el autor de la burbuja. */
function mediaTextColor(message: MessageView): string {
  if (message.authorType === 'bot') return '#25429b';
  if (message.authorType === 'system') return '#586a80';
  return '#0d4f3d';
}

function renderContent(message: MessageView): ReactElement {
  const media = message.media;

  switch (message.type) {
    case 'text':
      return <p className="bubble__text">{message.text ?? ''}</p>;

    case 'image':
      return renderMedia(message, 'image', 'Imagen');

    case 'audio':
      return renderMedia(message, 'audio', 'Audio');

    case 'video':
      return renderMedia(message, 'video', 'Video');

    case 'document':
      return renderMedia(message, 'document', 'Documento');

    case 'sticker':
      return renderMedia(message, 'sticker', 'Sticker');

    case 'location': {
      const latitude = typeof media?.latitude === 'number' ? media.latitude : null;
      const longitude = typeof media?.longitude === 'number' ? media.longitude : null;
      return (
        <>
          <MediaTag>Ubicacion</MediaTag>
          {latitude !== null && longitude !== null ? (
            <>
              <p className="media__meta">
                {latitude.toFixed(5)}, {longitude.toFixed(5)}
              </p>
              <a
                className="media__link"
                href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                Ver en el mapa
              </a>
            </>
          ) : (
            <p className="media__meta">Sin coordenadas</p>
          )}
        </>
      );
    }

    case 'contacts':
      return <MediaTag>Contacto compartido</MediaTag>;

    case 'interactive':
      return (
        <>
          <MediaTag>Mensaje interactivo</MediaTag>
          {message.text ? <p className="bubble__text">{message.text}</p> : null}
        </>
      );

    case 'button':
      return (
        <>
          <MediaTag>Boton pulsado</MediaTag>
          <p className="bubble__text">{message.text ?? message.reply?.title ?? ''}</p>
        </>
      );

    case 'template':
      return (
        <>
          <MediaTag>Plantilla</MediaTag>
          <p className="bubble__text">
            {message.template
              ? `${message.template.name} (${message.template.language})`
              : (message.text ?? 'Plantilla enviada')}
          </p>
        </>
      );

    case 'reaction':
      return (
        <>
          <MediaTag>Reaccion</MediaTag>
          <p className="bubble__text">{message.text ?? ''}</p>
        </>
      );

    case 'system':
      return <p className="bubble__text">{message.text ?? messagePreview(message)}</p>;

    case 'unsupported':
      return (
        <>
          <MediaTag>No soportado</MediaTag>
          <p className="media__meta">
            WhatsApp ha enviado un tipo de mensaje que el panel todavia no puede mostrar.
          </p>
        </>
      );
  }
}

export const MessageBubble = memo(function MessageBubble({ message }: { message: MessageView }): ReactElement {
  if (message.type === 'system') {
    return (
      <div className="systemrow">
        <span className="systemrow__text">{message.text ?? 'Aviso del sistema'}</span>
      </div>
    );
  }

  const outbound = message.direction === 'outbound';
  const authorLabel = message.authorName ?? AUTHOR_TYPE_LABELS[message.authorType];
  const classes = [
    'bubble',
    outbound ? 'bubble--out' : 'bubble--in',
    `bubble--author-${message.authorType}`,
    message.status === 'failed' ? 'bubble--failed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={outbound ? 'bubblerow bubblerow--out' : 'bubblerow bubblerow--in'}>
      <article className={classes}>
        <header className="bubble__meta">
          <span className="bubble__author">{authorLabel}</span>
          <span className="bubble__time" title={formatDateTime(message.createdAt)}>
            {formatTime(message.createdAt)}
          </span>
        </header>

        <div className="bubble__content">{renderContent(message)}</div>

        {message.reply ? (
          <p className="bubble__reply" title="Mensaje citado">
            Responde a: {message.reply.title}
          </p>
        ) : null}

        {message.status === 'failed' ? <MessageError message={message} /> : null}

        {outbound ? (
          <footer className="bubble__footer">
            <Tick message={message} />
          </footer>
        ) : null}
      </article>
    </div>
  );
});
